import React, { useEffect, useState } from "react";
import axios from "axios";
import splogo from "../assets/splogo.png";
import { openChatWindow } from "../components/ChatLaunchar"; // adjust path if needed
import { Loader2, Mail, MessageCircle, Calendar, ArrowUpRight, HelpCircle } from "lucide-react";

const SupportPage = () => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const url = "https://api.confidanto.com/header-data";

  const options = [
    {
      title: "Email Support",
      description: "Send us a ticket. We typically respond within 24 hours.",
      link: "mailto:kajal.a@confidanto.com",
      linkText: "kajal.a@confidanto.com",
      icon: <Mail className="w-6 h-6 text-indigo-600 transition-transform duration-300 group-hover:scale-110" />,
      isChat: false,
    },
    {
      title: "Live Chat",
      description: "Chat with our product specialists instantly for 24/7 support.",
      link: "#",
      linkText: "Chat now",
      icon: <MessageCircle className="w-6 h-6 text-emerald-600 transition-transform duration-300 group-hover:scale-110" />,
      isChat: true,
    },
    {
      title: "Schedule a Call",
      description: "Book a dedicated 1-on-1 strategy or troubleshooting meeting.",
      link: "https://calendar.app.google/pX4AMxaW8cdrTjaa7",
      linkText: "Book session",
      icon: <Calendar className="w-6 h-6 text-blue-600 transition-transform duration-300 group-hover:scale-110" />,
      isChat: false,
    },
  ];

  const handleClick = (item, e) => {
    if (item.isChat) {
      e.preventDefault();
      openChatWindow();
    }
  };

  const fetchInfo = async () => {
    try {
      setLoading(true);
      const email = localStorage.getItem("email");
      if (!email) {
        setError("User session not found. Please log in again.");
        return;
      }
      const res = await axios.post(url, { email });
      setUserData(res.data.userData);
      setError(null);
    } catch (err) {
      console.error("Error fetching user data:", err);
      setError("Unable to sync profile data, but you can still access channels below.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInfo();
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 flex flex-col font-sans selection:bg-indigo-500 selection:text-white relative overflow-hidden">
      {/* Background Subtle Tech Grid Decorator */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#e2e8f0_1px,transparent_1px),linear-gradient(to_bottom,#e2e8f0_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] opacity-60 pointer-events-none" />

      {/* Header */}
      {/* <header className="sticky top-0 bg-white/80 backdrop-blur-md border-b border-slate-200/80 text-slate-900 px-6 py-4 z-50 transition-all">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3.5">
            <div className="p-1 bg-gradient-to-tr from-indigo-600 to-violet-500 rounded-xl shadow-sm">
              <img
                src={splogo}
                alt="Confidanto Logo"
                className="h-9 w-9 object-contain bg-white rounded-lg p-1"
              />
            </div>
            <div>
              <h1 className="text-xl font-bold tracking-tight bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
                Confidanto
              </h1>
              <p className="text-xs text-slate-500 font-medium hidden sm:block">Customer Success Hub</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm font-medium text-slate-600 bg-slate-100 px-3 py-1.5 rounded-full border border-slate-200">
            <HelpCircle className="w-4 h-4 text-indigo-500" />
            <span>Support Center</span>
          </div>
        </div>
      </header> */}

      {/* Main Content Container */}
      <main className="max-w-5xl mx-auto flex-1 px-6 py-12 w-full relative z-10 flex flex-col justify-center">
        
        {/* User Profile Card Section */}
        <div className="mb-12">
          {loading ? (
            <div className="flex justify-center items-center py-6 bg-white rounded-2xl border border-slate-200/60 shadow-sm">
              <Loader2 className="animate-spin w-6 h-6 text-indigo-600" />
              <span className="ml-3 text-sm text-slate-500 font-medium">Syncing account state...</span>
            </div>
          ) : error ? (
            <div className="p-4 bg-amber-50 rounded-2xl border border-amber-200/70 flex items-center gap-3">
              <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse shrink-0" />
              <p className="text-sm text-amber-800 font-medium">{error}</p>
            </div>
          ) : (
            userData && (
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-6 bg-white rounded-2xl shadow-sm border border-slate-200/80 hover:border-slate-300 transition-colors duration-200">
                <div className="flex items-center gap-4">
                  <div className="rounded-xl bg-gradient-to-br from-blue-800 to-blue-900 text-white text-xl w-14 h-14 flex items-center justify-center font-bold shadow-inner uppercase">
                    {userData.username ? userData.username.charAt(0) : "U"}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-gray-900 text-base leading-none">
                        {userData.username}
                      </p>
                      <span className="px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-indigo-700 bg-indigo-50 rounded-md border border-indigo-100">
                        {userData.userType || "Client"}
                      </span>
                    </div>
                    <p className="text-slate-500 text-sm mt-1">{userData.email}</p>
                  </div>
                </div>
                <div className="text-xs text-slate-400 font-medium sm:text-right">
                  Authenticated Session
                </div>
              </div>
            )
          )}
        </div>

        {/* Hero Copy */}
        <section className="text-center max-w-2xl mx-auto space-y-4 mb-16">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight leading-tight">
            How can we help you build today?
          </h2>
          <p className="text-slate-600 text-base sm:text-lg max-w-xl mx-auto font-normal leading-relaxed">
            Get expert guidance with system setup, diagnostic reporting, UI customization, and high-performance campaign operations.
          </p>
        </section>

        {/* Action Channels Matrix */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {options.map((item, idx) => (
            <div
              key={idx}
              className="group flex flex-col justify-between p-6 bg-white border border-slate-200 rounded-2xl shadow-sm hover:shadow-xl hover:border-slate-300/80 transition-all duration-300 transform hover:-translate-y-1"
            >
              <div>
                {/* Header Icon container */}
                <div className="w-12 h-12 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center mb-5 group-hover:bg-white group-hover:shadow-sm transition-all duration-300">
                  {item.icon}
                </div>
                
                <h3 className="text-lg font-bold text-slate-900 tracking-tight">
                  {item.title}
                </h3>
                <p className="text-slate-500 text-sm mt-2 leading-relaxed">
                  {item.description}
                </p>
              </div>

              {/* Action Button */}
              <div className="mt-6 pt-4 border-t border-slate-50">
                <a
                  href={item.link}
                  onClick={(e) => handleClick(item, e)}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-600 hover:text-indigo-700 group/btn transition-colors duration-150"
                >
                  <span>{item.linkText}</span>
                  <ArrowUpRight className="w-3.5 h-3.5 transform group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5 transition-transform" />
                </a>
              </div>
            </div>
          ))}
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-white text-center py-6 mt-12 border-t border-slate-200/60 relative z-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-slate-400 font-medium order-2 sm:order-1">
            © {new Date().getFullYear()} Confidanto Inc. All rights reserved.
          </p>
          <div className="flex items-center gap-6 text-xs text-slate-400 font-medium order-1 sm:order-2">
            <a href="#" className="hover:text-slate-600 transition-colors">Privacy Policy</a>
            <a href="#" className="hover:text-slate-600 transition-colors">Terms of Service</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SupportPage;